import { SimpleUserAvatar } from '@/components/UserAvatar'
import SimpleUsername from '@/components/SimpleUsername'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { cn } from '@/lib/utils'

export type TArgsListItem =
  | { kind: 'choice'; value: string }
  | { kind: 'pubkey'; value: string }
  | { kind: 'relay'; value: string }
  | { kind: 'text'; value: string }

export interface ArgsListProps {
  items: TArgsListItem[]
  argLabel?: string
  command: (item: TArgsListItem) => void
}

export interface ArgsListHandle {
  onKeyDown: (args: SuggestionKeyDownProps) => boolean
}

function ItemContent({ item }: { item: TArgsListItem }) {
  switch (item.kind) {
    case 'choice':
      return <span className="font-mono text-sm">{item.value}</span>
    case 'pubkey':
      return (
        <div className="flex gap-2 items-center w-72 min-w-0">
          <SimpleUserAvatar user={item.value} />
          <div className="flex-1 w-0 min-w-0">
            <SimpleUsername user={item.value} className="font-semibold truncate" />
            <div className="text-xs text-muted-foreground font-mono truncate">{item.value}</div>
          </div>
        </div>
      )
    case 'relay':
      return <span className="text-sm truncate">{item.value}</span>
    case 'text':
      return (
        <span className="text-sm text-muted-foreground">
          {item.value ? `"${item.value}"` : 'Use typed text'}
        </span>
      )
  }
}

const ArgsList = forwardRef<ArgsListHandle, ArgsListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectItem = (index: number) => {
    const item = props.items[index]
    if (item) props.command(item)
  }

  const upHandler = () =>
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
  const downHandler = () => setSelectedIndex((selectedIndex + 1) % props.items.length)
  const enterHandler = () => selectItem(selectedIndex)

  useEffect(() => {
    setSelectedIndex(props.items.length ? 0 : -1)
  }, [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }
      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }
      if (event.key === 'Enter' && selectedIndex >= 0) {
        enterHandler()
        return true
      }
      return false
    }
  }))

  if (!props.items.length) return null

  return (
    <div className="border rounded-lg bg-background z-50 pointer-events-auto flex flex-col max-h-80 overflow-y-auto shadow-md">
      {props.argLabel && (
        <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground font-medium">
          {props.argLabel}
        </div>
      )}
      {props.items.map((item, index) => (
        <button
          className={cn(
            'cursor-pointer text-start items-center m-1 p-2 outline-none transition-colors rounded-md',
            selectedIndex === index && 'bg-accent text-accent-foreground'
          )}
          key={`${item.kind}:${item.value}:${index}`}
          onClick={() => selectItem(index)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="flex gap-2 items-center w-80 max-w-full truncate pointer-events-none">
            <ItemContent item={item} />
          </div>
        </button>
      ))}
    </div>
  )
})
ArgsList.displayName = 'ArgsList'
export default ArgsList
